import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { WORKFLOW_STATUSES, SALE_DESTINATIONS } from './recon/constants';

const RUNLIST_CATEGORIES = [
  { value: 'mech_shop', label: 'Mech Shop' },
  { value: 'outside_repairs', label: 'Outside Repairs' },
  { value: 'detail', label: 'Detail' },
  { value: 'chris', label: 'Chris' },
  { value: 'done', label: 'Done' },
];

const RunListPage = () => {
  const { currentUser } = useAuth();
  const [runList, setRunList] = useState([]);
  const [customDestinations, setCustomDestinations] = useState([]);
  const [showFinancials, setShowFinancials] = useState(false);
  const [viewMode, setViewMode] = useState('category');

  useEffect(() => {
    try {
      setRunList(JSON.parse(localStorage.getItem('workflow_runlist') || '[]'));
      setCustomDestinations(JSON.parse(localStorage.getItem('workflow_custom_destinations') || '[]'));
    } catch {
      setRunList([]);
      setCustomDestinations([]);
    }
  }, []);

  const allDestinations = [...SALE_DESTINATIONS, ...customDestinations];

  const groupedByDestination = allDestinations.map(dest => ({
    ...dest,
    vehicles: runList.filter(v => v.destination === dest.value)
  })).filter(g => g.vehicles.length > 0);

  const groupedByCategory = [
    ...RUNLIST_CATEGORIES.map(cat => ({
      ...cat,
      vehicles: runList.filter(v => v.assignee === cat.value)
    })),
    { value: 'unassigned', label: 'Unassigned', vehicles: runList.filter(v => !v.assignee || !RUNLIST_CATEGORIES.some(c => c.value === v.assignee)) }
  ].filter(g => g.vehicles.length > 0);

  const activeGroups = viewMode === 'category' ? groupedByCategory : groupedByDestination;

  const totalInvestment = runList.reduce((sum, v) => sum + (v.tiv || 0), 0);
  const totalPotentialProfit = runList.reduce((sum, v) => sum + (v.potentialProfit || 0), 0);

  const isAdmin = currentUser?.role === 'admin';

  return (
    <div className="min-h-screen bg-black text-green-400 font-mono">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-green-500 print:hidden">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-white">WEEKLY RUN LIST</h1>
          <div className="flex bg-neutral-800 rounded overflow-hidden">
            <button onClick={() => setViewMode('category')}
              className={`px-3 py-1 text-xs font-bold ${viewMode === 'category' ? 'bg-green-600 text-black' : 'text-neutral-400 hover:text-white'}`}>
              BY CATEGORY
            </button>
            <button onClick={() => setViewMode('master')}
              className={`px-3 py-1 text-xs font-bold ${viewMode === 'master' ? 'bg-green-600 text-black' : 'text-neutral-400 hover:text-white'}`}>
              MASTER LIST
            </button>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {isAdmin && (
            <label className="flex items-center gap-2 text-green-400 text-sm cursor-pointer">
              <input type="checkbox" checked={showFinancials} onChange={(e) => setShowFinancials(e.target.checked)} className="rounded" />
              Show Financials
            </label>
          )}
          <button onClick={() => window.print()} disabled={runList.length === 0}
            className="px-4 py-2 bg-green-600 text-black text-sm font-bold rounded hover:bg-green-500 disabled:opacity-50">
            PRINT
          </button>
          <a href="/" className="px-4 py-2 bg-neutral-700 text-white text-sm font-bold rounded hover:bg-neutral-600">BACK TO APP</a>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 text-sm">
        {runList.length === 0 ? (
          <div className="text-center text-green-500 py-20">
            <div className="text-2xl mb-2">[ EMPTY ]</div>
            <div className="text-neutral-500">No vehicles in run list.</div>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto">
            <div className="text-neutral-500 mb-4 print:text-black">LOTSENSE DMS • {new Date().toLocaleDateString()} • {viewMode === 'category' ? 'BY CATEGORY' : 'MASTER LIST'}</div>
            
            {activeGroups.map(group => (
              <div key={group.value} className="mb-8">
                <div className="text-green-400 text-xl font-bold mb-3 border-b border-green-500 pb-1 print:text-black print:border-black">
                  {group.label.toUpperCase()}
                  <span className="text-neutral-500 text-sm ml-2 print:text-black">({group.vehicles.length})</span>
                </div>
                <div className="border-l-2 border-green-800 pl-4 print:border-black">
                  {group.vehicles.map(v => {
                    const catLabel = RUNLIST_CATEGORIES.find(c => c.value === v.assignee)?.label;
                    return (
                    <div key={v.id} className="py-2 border-b border-neutral-800 print:border-gray-300">
                      <div>
                        <span className="text-white font-bold print:text-black">[{v.stockNumber}]</span>
                        <span className="text-green-400 ml-2 print:text-black">{v.year} {v.make} {v.model}</span>
                        {viewMode === 'category' && <span className="text-neutral-500 ml-2 print:text-black print:font-bold">→ {v.destination || 'N/A'}</span>}
                        {viewMode === 'master' && catLabel && <span className="text-orange-400 font-bold ml-2 print:text-black">[{catLabel}]</span>}
                      </div>
                      <div className="text-yellow-400 print:text-black">NEEDS: {Array.isArray(v.needs) ? v.needs.map(n => WORKFLOW_STATUSES.find(s => s.value === n)?.label || n).join(' / ') : (WORKFLOW_STATUSES.find(s => s.value === v.needs)?.label || v.needs)}</div>
                      {v.runListNotes && <div className="text-neutral-400 italic text-xs print:text-black print:font-bold">TASK: {v.runListNotes}</div>}
                      {v.notes && <div className="text-neutral-500 italic text-xs print:text-black">GAME PLAN: {v.notes}</div>}
                      <div className="text-neutral-500 print:text-black print:font-bold">LOC: {v.location}</div>
                      {showFinancials && (
                        <div className="mt-1 text-xs grid grid-cols-5 gap-2 print:text-black">
                          <div><span className="text-neutral-600">COST:</span> <span className="text-cyan-400 print:text-black">${(v.deliveredCost || 0).toLocaleString()}</span></div>
                          <div><span className="text-neutral-600">RECON:</span> <span className="text-cyan-400 print:text-black">${(v.reconCost || 0).toLocaleString()}</span></div>
                          <div><span className="text-neutral-600">TIV:</span> <span className="text-cyan-400 print:text-black">${(v.tiv || 0).toLocaleString()}</span></div>
                          <div><span className="text-neutral-600">ASK:</span> <span className="text-cyan-400 print:text-black">${(v.askingPrice || 0).toLocaleString()}</span></div>
                          <div><span className="text-neutral-600">PROFIT:</span> <span className={`${v.potentialProfit >= 0 ? 'text-green-400' : 'text-red-400'} print:text-black`}>${(v.potentialProfit || 0).toLocaleString()}</span></div>
                        </div>
                      )}
                    </div>
                  );})}
                  <div className="text-neutral-500 mt-2 print:text-black print:font-bold">TOTAL: {group.vehicles.length} vehicle{group.vehicles.length !== 1 ? 's' : ''}</div>
                </div>
              </div>
            ))}

            {/* Summary */}
            <div className="border-t-2 border-green-500 pt-4 mt-8 print:border-black">
              <div className="text-white font-bold mb-2 print:text-black">═══════════════════════════════════════════</div>
              <div className="text-white font-bold print:text-black">SUMMARY</div>
              <div className="text-green-400 print:text-black">Total Vehicles: {runList.length}</div>
              {RUNLIST_CATEGORIES.map(cat => {
                const count = runList.filter(v => v.assignee === cat.value).length;
                return count > 0 ? <div key={cat.value} className="text-green-400 print:text-black">{cat.label}: {count}</div> : null;
              })}
              {(() => { const unCount = runList.filter(v => !v.assignee || !RUNLIST_CATEGORIES.some(c => c.value === v.assignee)).length; return unCount > 0 ? <div className="text-green-400 print:text-black">Unassigned: {unCount}</div> : null; })()}
              {showFinancials && (
                <div className="mt-4 pt-4 border-t border-green-800 print:border-gray-300">
                  <div className="text-cyan-400 print:text-black">Total Investment: ${totalInvestment.toLocaleString()}</div>
                  <div className={`${totalPotentialProfit >= 0 ? 'text-green-400' : 'text-red-400'} print:text-black`}>
                    Total Potential Profit: ${totalPotentialProfit.toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .print\\:text-black { color: #000 !important; }
          .print\\:font-bold { font-weight: 900 !important; }
          .print\\:border-black { border-color: black !important; }
          .print\\:border-gray-300 { border-color: #ccc !important; }
        }
      `}</style>
    </div>
  );
};

export default RunListPage;
